import filesize from "filesize";
import "./FileUploader.scss";
import { Accept, DropEvent, FileRejection, useDropzone } from "react-dropzone";
import { AvailableFileTypes, DefaultAcceptedFileType, FILE_UPLOADER_DEFAULT_MAX_SIZE_IN_BYTES } from "utilities/Constant";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import { useCallback, useMemo, useRef, type ChangeEvent } from "react";
import { Button } from "../button/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { regular } from "@fortawesome/fontawesome-svg-core/import.macro";

export interface FileUploaderProps {
  maxSizeInBytes?: number;
  allowedFileTypes?: string[];
  maxFiles?: number;
  uploadedFileCount?: number;
  onDrop?: (acceptedFiles: File[], fileRejections: FileRejection[], event: DropEvent) => void;
  isDropZoneDisabled?: boolean;
}

export const GetAcceptableFileTypes = (allowedUploadFileTypes: string): Accept => {
  if (!allowedUploadFileTypes) return DefaultAcceptedFileType;

  const allowedTypesArray = allowedUploadFileTypes.split(",").map((type) => type.trim().toLowerCase());

  return Object.fromEntries(
    Object.entries(AvailableFileTypes).filter(([, extensions]) =>
      extensions.some((ext) => allowedTypesArray.includes(ext.substring(1).toLowerCase()))
    )
  );
};

const getFileExtension = (file: File): string => {
  const { name } = file;
  if (!name.includes(".")) return "";
  const ext = name.split(".").pop();
  return ext ? `.${ext.toLowerCase()}` : "";
};

/** Some mobile cameras return empty or odd names; map common image MIME types to extensions we accept. */
const extensionFromMime = (mime: string): string => {
  const m = mime.toLowerCase().split(";")[0].trim();
  if (m === "image/jpeg" || m === "image/jpg") return ".jpg";
  if (m === "image/png") return ".png";
  if (m === "image/tiff") return ".tif";
  if (m === "image/emf") return ".emf";
  return "";
};

const effectiveExtension = (file: File): string => {
  const fromName = getFileExtension(file);
  if (fromName) return fromName;
  return extensionFromMime(file.type);
};

/** Android Chrome often returns numeric names with no "." and MIME empty or application/octet-stream for JPEG camera output. */
const withJpegNameForCameraCapture = (file: File): File => {
  const mime = (file.type || "").toLowerCase().split(";")[0].trim();
  if (mime && mime !== "application/octet-stream" && !mime.startsWith("image/")) return file;
  if (mime === "image/png" || mime === "image/tiff" || mime === "image/emf") return file;
  if (mime === "image/heic" || mime === "image/heif") return file;

  const stem = file.name.includes(".") ? file.name.slice(0, file.name.lastIndexOf(".")) : file.name;
  const safe = stem.replace(/[^\w.-]/g, "_").replace(/^\.+/, "").slice(0, 120) || "photo";
  return new File([file], `${safe}.jpg`, { type: "image/jpeg", lastModified: file.lastModified });
};

export default function FileUploader({
  maxSizeInBytes = FILE_UPLOADER_DEFAULT_MAX_SIZE_IN_BYTES,
  allowedFileTypes = ["emf", "jpg", "jpeg", "pdf"],
  maxFiles = 12,
  uploadedFileCount = 0,
  onDrop,
  isDropZoneDisabled,
}: Readonly<FileUploaderProps>) {
  const allowedExtensions = useMemo(
    () => Object.values(AvailableFileTypes).flat().map((ext) => ext.toLowerCase()),
    []
  );

  const finalizeFiles = useCallback(
    (incomingFiles: File[], priorRejections: FileRejection[], event: DropEvent) => {
      const trulyAccepted: File[] = [];
      const trulyRejected: FileRejection[] = [...priorRejections];

      incomingFiles.forEach((file) => {
        const ext = effectiveExtension(file);
        if (allowedExtensions.includes(ext)) {
          trulyAccepted.push(file);
        } else {
          trulyRejected.push({
            file,
            errors: [
              {
                code: "file-invalid-type",
                message: `Files with extension "${ext || "(unknown)"}" are not supported.`,
              },
            ],
          });
        }
      });

      onDrop?.(trulyAccepted, trulyRejected, event);
    },
    [allowedExtensions, onDrop]
  );

  const { getRootProps, getInputProps, open } = useDropzone({
    disabled: isDropZoneDisabled,
    accept: AvailableFileTypes,
    maxSize: maxSizeInBytes,
    noClick: true,
    onDrop: (acceptedFiles, fileRejections, event) => {
      finalizeFiles(acceptedFiles, fileRejections, event);
    },
  });
  const { data: featureFlag } = useGetFeatureFlagsQuery();
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const handleTakePhotoClick = () => cameraInputRef.current?.click();

  const handleCameraInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files ? Array.from(event.target.files) : [];
      event.target.value = "";

      const sizeRejections: FileRejection[] = [];
      const sizeOk: File[] = [];
      files.forEach((file) => {
        if (file.size > maxSizeInBytes) {
          sizeRejections.push({
            file,
            errors: [
              {
                code: "file-too-large",
                message: "File is larger than 40 MB",
              },
            ],
          });
        } else {
          const normalized =
            allowedExtensions.includes(effectiveExtension(file)) ? file : withJpegNameForCameraCapture(file);
          sizeOk.push(normalized);
        }
      });

      finalizeFiles(sizeOk, sizeRejections, event);
    },
    [allowedExtensions, finalizeFiles, maxSizeInBytes]
  );

  return (
    <div className="file-uploader">
      <div className="file-uploader__content">
        <div className="file-uploader__file-count">
          <label className="label-medium-medium">
            {uploadedFileCount}/{maxFiles} files max
          </label>
        </div>

        <div className="file-uploader__dropzone">
          <div {...getRootProps()} className={isDropZoneDisabled ? "dropzone disabled" : "dropzone"}>
            <input {...getInputProps()} />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              multiple={false}
              tabIndex={-1}
              aria-hidden
              disabled={isDropZoneDisabled}
              style={{ display: "none" }}
              onChange={handleCameraInputChange}
            />
            <div className="file-uploader__instructions">
              <span className="label-large-medium desktop-only">
                Drag or drop files here
                <br /> Or
              </span>

              <div className="upload-button">
                <Button
                  onClick={!isDropZoneDisabled ? open : undefined}
                  label="Attach File"
                  variant="outline"
                  icon={<FontAwesomeIcon icon={regular("paperclip-vertical")} />}
                />
                {featureFlag?.cameraEnabled && (
                  <Button
                    className="mobile-only"
                    onClick={!isDropZoneDisabled ? handleTakePhotoClick : undefined}
                    label="Take Photo"
                    variant="outline"
                    icon={<FontAwesomeIcon icon={regular("camera")} />}
                  />
                )}
              </div>

              <div>
                <i className="hint-text">Supported files:</i>
                <label className="label-medium-medium hint-text">{allowedFileTypes.map((t) => `.${t}`).join(", ")}</label>
                <i className="hint-text">Max size:</i>
                <label className="label-medium-medium hint-text">40MB per file</label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
